import type { EnhancedProductFamily, EnhancedProductVariant } from '#api/enhancements/enhancedTmsApiTypes'
import type { useAppNavigation } from '#app/navigation/NavigationContext'
import type { TruckConfiguration } from '#app/store/slices/configurationSlice'
import type { AppInsightsData, AppProductFamily, AppProductGroup, AppProductVariant } from '#app/types/AppTypes'
import { familyInitialSelection, isVariantSelected } from '#app/types/properties'
import { CosimaDocuments } from '#features/Cosima/loader'
import { getCosimaImages } from '#features/Cosima/util'
import {
  makeBuildVerificationLocation,
  makeComparisonConfigurationLocation,
  makeDefaultConfigurationLocation,
  makeHistoryConfigurationLocation,
  makeOptionalEquipmentLocation,
  makePrintConfigurationLocation,
  makeTechnicalDataLocation,
} from '../components/ConfigurationNavigation'
import { tireGroupEntries } from '../TireEditor/tireIds'
import { getAllGroups } from './collections'

export const getCosimaDocument = (variant: EnhancedProductVariant, family: EnhancedProductFamily, language: 'en-GB' | 'de-DE') => {
  const code = `${family.id}${variant.id}`
  return CosimaDocuments[code]?.languages[language]
}

export type ViewMode = 'details' | 'print' | 'comparison' | 'technical-data' | 'build-verification' | 'history' | 'changes' | 'optional-equipment'
export type Step2ViewModeRequester = ReturnType<typeof onViewRequestedCallback>

export const onViewRequestedCallback = (navigate: ReturnType<typeof useAppNavigation>['navigate'], configuration: TruckConfiguration | undefined) => {
  return (mode: ViewMode) => {
    if (!configuration) {
      return
    }
    switch (mode) {
      case 'build-verification':
        return navigate(makeBuildVerificationLocation(configuration))
      case 'history':
        return navigate(makeHistoryConfigurationLocation(configuration))
      case 'details':
        return navigate(makeDefaultConfigurationLocation(configuration))
      case 'print':
        return navigate(makePrintConfigurationLocation(configuration))
      case 'comparison':
        return navigate(makeComparisonConfigurationLocation(configuration))
      case 'technical-data':
        return navigate(makeTechnicalDataLocation(configuration))
      case 'optional-equipment':
        return navigate(makeOptionalEquipmentLocation(configuration))
    }
    console.warn(`Unknown view mode requested: ${mode}`)
    return
  }
}

const variantMetaData = (configuration: TruckConfiguration, family: AppProductFamily, variant: AppProductVariant) => {
  // not in cache -> not loaded
  const enrichedVariant = variant.enrichment.enrichedVariants[`${family.id}${variant.id}`]

  const isLoaded = !!enrichedVariant // !!meta.loadedFamilyVariants[family.id]
  const hasDetails = !!enrichedVariant?.detailData

  const hasInsights = anyFieldDefinedAndNotFalse(variant.insightsData)

  // selected variant overwrites the initial selection; otherwise the default is selected (or just nothing)
  const isSelected = isVariantSelected(family, variant, configuration.configuredFamilies[family.id])
  const isDefault = family.initialSelectedVariant?.id === variant.id
  // the url slug decides if the variant is active
  const highlight = configuration.viewHighlight.panel
  const isActive = highlight ? highlight.familyId === variant.parentFamily.id && highlight.variantId === variant.id : false

  const isChanging =
    configuration.history?.some(
      (x) =>
        (x.status === 'pending' || x.status === 'queued') && x.selections.some((s) => s.family.id === family.id && s.toVariant.id === variant.id),
    ) ?? false

  const cosima = variant.cosima?.details
  const description =
    variant?.detailData?.description ??
    variant.parentFamily.detailData?.description ??
    cosima?.shortDescription ??
    cosima?.description ??
    cosima?.benefits ??
    cosima?.functions

  const resources = getCosimaImages(variant.cosima)

  return {
    isLoaded,
    hasDetails,
    isSelected,
    isDefault,
    isActive,
    isChanging,
    hasInsights,
    /** A description from either the variant detail, it's family detail or the cosima document  */
    description,
    /** A collection of all resources of that variant or from its cosima document */
    resources,
  }
}

function mergeInsights(insightData: AppInsightsData | undefined, newData: AppInsightsData | undefined): AppInsightsData | undefined {
  if (!insightData) {
    return newData
  }
  if (!newData) {
    return insightData
  }

  return {
    topOption: insightData.topOption || newData.topOption,
    commissionRelevant: insightData.commissionRelevant || newData.commissionRelevant,
    orderRelevant: insightData.orderRelevant || newData.orderRelevant,
    relevantForMaintenanceAndRepairBenefits: insightData.relevantForMaintenanceAndRepairBenefits || newData.relevantForMaintenanceAndRepairBenefits,
    relevantForResidualValue: insightData.relevantForResidualValue || newData.relevantForResidualValue,
  }
}

function anyFieldDefinedAndNotFalse(insightData: AppInsightsData | undefined): boolean {
  if (!insightData) {
    return false
  }
  return Object.values(insightData).some((x) => x !== false && x !== undefined && x !== null)
}

const familyMetaData = (configuration: TruckConfiguration, family: AppProductFamily) => {
  const enrichedFamilyData = family.enrichment.enrichedFamilies[family.id]
  const isLoaded = !!enrichedFamilyData
  const isLocked = configuration.configuredFamilies[family.id]?.isLocked ?? false
  const hasDetails = !!enrichedFamilyData?.detailData
  // collect insights from all variants
  let mergedInsights: undefined | AppInsightsData
  for (const variant of family.variants) {
    mergedInsights = mergeInsights(mergedInsights, variant.insightsData)
  }
  const isReadOnly = family.totalVariantCount < 2
  const isChanging =
    configuration.history?.some((x) => (x.status === 'pending' || x.status === 'queued') && x.selections.some((s) => s.family.id === family.id)) ??
    false
  // @TODO: active property should be refactored into "highlighted" and checked against other highlight types
  const isActive = configuration.viewHighlight.panel?.familyId === family.id

  return {
    isLocked,
    isLoaded,
    hasDetails,
    hasInsights: anyFieldDefinedAndNotFalse(mergedInsights),
    insightData: mergedInsights,
    hasSelectedInsights: anyFieldDefinedAndNotFalse(family.selectedVariant?.insightsData),
    isReadOnly,
    isActive,
    isChanging,
  }
}

export const makeFamilyMetaData = (
  family: AppProductFamily,
  isFiltered: boolean,
  isExpanded: 'by-family-expansion' | 'by-search-expansion' | false,
  configuration: TruckConfiguration,
) => {
  const familyData = familyMetaData(configuration, family)
  const selectedVariant = isExpanded
    ? family.variants[0]
    : (family.selectedVariant ?? familyInitialSelection(family) ?? family.initialSelectedVariant)
  return {
    type: 'familyMetaData',
    familyExtraData: familyData,
    isFiltered,
    isLoading: false, // !familyData.isLoaded,
    hasDetails: false, // no detail API
    family: family,
    isFamilyExpanded: isExpanded,
    /** Fallback variant tries to select the `selectedVariant`, the family initial selection or the initial selection */
    fallbackVariant: selectedVariant,
    variantExtraData: selectedVariant ? variantMetaData(configuration, family, selectedVariant) : undefined,
    isChanging: familyData.isChanging,
  }
}

export const makeVariantMetaData = (
  variant: AppProductVariant,
  isFiltered: boolean,
  isExpanded: 'by-family-expansion' | 'by-search-expansion' | false,
  configuration: TruckConfiguration,
) => {
  const familyData = familyMetaData(configuration, variant.parentFamily)
  const variantData = variantMetaData(configuration, variant.parentFamily, variant)
  return {
    type: 'variantMetaData',
    familyExtraData: familyData,
    variantExtraData: variantData,
    isFiltered,
    isLoading: false, // !(familyData.isLoaded && variantData.isLoaded),
    hasDetails: false, // no detail API
    fallbackVariant: variant,
    family: variant.parentFamily,
    isFamilyExpanded: isExpanded,
    variant: variant,
    isChanging: variantData.isChanging,
  }
}

export const makeGroupMetaData = (group: AppProductGroup) => {
  return {
    button: group.id === tireGroupEntries.id ? ('tire-editor' as const) : undefined,
  } as const
}

export const createInitialSelection = (configuration: TruckConfiguration): Exclude<TruckConfiguration['initialSelection'], undefined> => {
  const initialSelection: TruckConfiguration['initialSelection'] = structuredClone(configuration.initialSelection) ?? {}

  for (const group of getAllGroups(configuration.productGroups)) {
    for (const family of group.families ?? []) {
      if (family.id in initialSelection) {
        continue
      }
      const variant = family.variants.find((x) => x.isSelected)
      if (!variant) {
        continue
      }
      initialSelection[family.id] = variant.id
    }
  }
  return initialSelection
}
